/**
 * Экран режима «Мои ошибки».
 *
 * Стадии цикла показаны явно: пользователь видит, где он сейчас и что будет
 * дальше. Непрозрачный тренажёр не объясняет, почему стало легче (ТЗ §7.4).
 *
 * Над текстом стоят только текущая стадия, разбираемое слово и — когда шаг
 * этого требует — его части: их читают одновременно с набором. Порядок шагов
 * целиком раскрывается под текстом (ТЗ FR-EN-84).
 */

import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Card, EmptyState } from '@type2x/ui';
import { TYPED_STAGES, remainingDrills, type MistakesSession } from '@type2x/feature-my-mistakes';

import type { SessionId } from '@type2x/shared-contracts';

import type { Runtime } from '../runtime.js';
import { currentStageTask } from '../state/mistakes.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface MistakesScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly session: MistakesSession;
  /** Идентификатор всей микросессии: стадии — её части, а не отдельные сессии. */
  readonly sessionId: SessionId;
  readonly onStageDone: (run: TypingSession) => void;
}

export function MistakesScreen({
  runtime,
  t,
  session,
  sessionId,
  onStageDone,
}: MistakesScreenProps): React.JSX.Element {
  const task = currentStageTask(session);
  if (task === null) {
    return (
      <Card title={t('mistakes.title')}>
        <EmptyState title={t('mistakes.emptyTitle')} body={t('mistakes.empty')} />
      </Card>
    );
  }

  const done = session.drills.length - remainingDrills(session).length;

  return (
    <DrillLayout
      t={t}
      title={t('mistakes.title')}
      now={
        <>
          <span className="t2x-drill__stage">{t(`practice.stage.${task.stage}`)}</span>{' '}
          <span className="t2x-muted">{t('mistakes.hint', { word: task.word })}</span>
          {task.showsChunks && (
            <span className="t2x-chunks" aria-label={t('mistakes.chunks')}>
              {task.chunks.join(' · ')}
            </span>
          )}
        </>
      }
      progress={{
        value: session.drills.length === 0 ? 0 : done / session.drills.length,
        label: t('mistakes.progress'),
      }}
      details={
        <>
          {/* Подпись видимая: список в рамках читался как ряд неработающих
              кнопок, хотя это порядок шагов, а не выбор (ТЗ FR-EN-68). */}
          <p className="t2x-muted">{t('mistakes.stagesHint')}</p>

          <ol className="t2x-stages" aria-label={t('mistakes.stages')}>
            {TYPED_STAGES.map((stage) => (
              <li
                key={stage}
                aria-current={stage === task.stage ? 'step' : undefined}
                className={stage === task.stage ? 't2x-stage-current' : undefined}
              >
                {t(`practice.stage.${stage}`)}
              </li>
            ))}
          </ol>

          <p className="t2x-muted">
            {t('mistakes.remaining', { count: remainingDrills(session).length })}
          </p>

          <p className="t2x-muted">{t(`mistakes.stage.${task.stage}`, { word: task.word })}</p>
        </>
      }
    >
      <TypingScreen
        // Ключ включает стадию и номер показа: возврат на ту же стадию — это
        // новый прогон, а не продолжение прежнего.
        key={`${task.word}:${task.stage}:${stageAttempt(session)}`}
        runtime={runtime}
        t={t}
        title={t(`practice.stage.${task.stage}`)}
        hint={t(`mistakes.stage.${task.stage}`, { word: task.word })}
        sessionId={sessionId}
        layoutId={session.layoutId}
        targetText={task.text}
        hintLevel={task.hintLevel}
        compact
        onFinished={onStageDone}
      />
    </DrillLayout>
  );
}

/** Сколько раз слово уже показывалось: различает повторные подходы. */
function stageAttempt(session: MistakesSession): number {
  return session.drills[session.currentIndex]?.shows ?? 0;
}
